import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import dayjs from 'dayjs';

import { KEYS_QUERY } from 'common/constants';
import useService from 'common/hook/useService';
import useCurrentPlanningPeriod from 'common/hook/useCurrentPlanningPeriod';
import {
  SCHOOL_YEAR_FIRST_MONTH,
  buildSchoolYearMonthOptions,
  dedupeFramesByMonth,
  pickDefaultMonthKey,
} from 'common/utils/schoolYearMonths';
import type { MonthlyMenuFrame, MonthlyMenuFramesResponse } from 'common/types/menuPlan';

interface Options {
  /** Năm học (KHÔNG phải năm dương lịch). Bỏ trống = năm học hiện hành suy từ lịch hệ thống. */
  year?: number;
  semester?: 1 | 2;
  enabled?: boolean;
}

/**
 * Năm học hiện hành + khung thực đơn tháng, dùng cho ô chọn Tháng/Tuần/Ngày ở Dashboard và các màn
 * của Bản đồ thực đơn.
 *
 * Phân vai rõ ràng theo docx 2.6: NĂM HỌC và danh sách THÁNG là của Kỳ kinh doanh — suy tại chỗ từ
 * lịch hệ thống, nên combo tháng vẫn đầy đủ kể cả khi MenuPlan chết. Còn TUẦN và NGÀY thì bắt buộc
 * lấy theo khung thực đơn của MenuPlan, không được tự tính.
 *
 * Truyền `year` tường minh sang MenuPlan: tham số đó của họ mặc định là năm DƯƠNG LỊCH hôm nay, để
 * trống thì từ tháng 1 đến tháng 8 sẽ hỏi nhầm sang năm học sau và nhận về rỗng.
 *
 * BE proxy sang MenuPlan (`/external/menuplan/monthly-menu-frames`) vì MenuPlan không mở CORS và
 * token của họ không được nhúng vào bundle FE.
 */
const useMonthlyMenuFrames = ({ year, semester, enabled = true }: Options = {}) => {
  const service = useService();

  const today = dayjs();
  const calendarMonth = today.month() + 1;
  // Năm học lấy từ Kỳ kinh doanh của KIOS (nguồn chuẩn theo docx 2.6); hook đó tự lo đường lùi.
  const { schoolYear: currentSchoolYear, schoolYearLabel } = useCurrentPlanningPeriod();
  const schoolYear = year ?? currentSchoolYear;
  /**
   * Tháng 8 là lúc MenuPlan đã đẩy khung tháng 9 của năm học SAU sang. Khung đó nằm ở năm học
   * schoolYear + 1 nên phải hỏi thêm một lượt, không thì T9/N+1 có trong combo mà không có tuần.
   */
  const bridgeNextYear = calendarMonth === SCHOOL_YEAR_FIRST_MONTH - 1;

  const fetchFrames = async (schoolYearToFetch: number) => {
    if (!service) return null;
    return await service.MenuPlanController_getMonthlyMenuFrames({
      queries: { year: schoolYearToFetch, semester },
    });
  };

  const query = useQuery({
    queryKey: [KEYS_QUERY.monthlyMenuFrames, schoolYear, semester],
    queryFn: async (): Promise<MonthlyMenuFramesResponse | null> => fetchFrames(schoolYear),
    // FE KHÔNG cache: lúc MenuPlan đang sửa khung thì mở lại màn là phải thấy ngay. Chặn gọi
    // trùng đã có cache của BE lo.
    staleTime: 0,
    enabled: !!service && enabled,
  });

  const bridgeQuery = useQuery({
    queryKey: [KEYS_QUERY.monthlyMenuFrames, schoolYear + 1, semester],
    queryFn: async (): Promise<MonthlyMenuFramesResponse | null> => fetchFrames(schoolYear + 1),
    staleTime: 0,
    enabled: !!service && enabled && bridgeNextYear,
  });

  // Gộp trùng NGAY ở đây để mọi chỗ dùng (ô Tháng, ô Tuần, tra khung) đều thấy cùng một tập khung.
  const frames = useMemo<MonthlyMenuFrame[]>(
    () => dedupeFramesByMonth([...(query.data?.frames ?? []), ...(bridgeQuery.data?.frames ?? [])]),
    [query.data?.frames, bridgeQuery.data?.frames]
  );

  const abridgedWeekTitles = useMemo(
    () => ({
      ...(query.data?.abridged_week_titles ?? {}),
      ...(bridgeQuery.data?.abridged_week_titles ?? {}),
    }),
    [query.data?.abridged_week_titles, bridgeQuery.data?.abridged_week_titles]
  );

  const monthOptions = useMemo(
    () => buildSchoolYearMonthOptions(schoolYear, calendarMonth),
    [schoolYear, calendarMonth]
  );

  return {
    ...query,
    frames,
    abridgedWeekTitles,
    monthOptions,
    defaultMonthKey: pickDefaultMonthKey(monthOptions, frames, today.format('YYYY-MM')),
    schoolYear,
    /** Nhãn năm học để hiển thị, VD "N 2026 - 2027" — đúng tên KIOS đặt. */
    schoolYearLabel,
    semesterLabel: query.data?.semester_label,
  };
};

export default useMonthlyMenuFrames;
